<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { JOB_FEATURE } from '@/helpers/constants'
import { priceWithDiscount } from '@/helpers/pricing'

import JobWizardStepFeaturesTabsContent from './JobWizardStepFeaturesTabsContent.vue'
import JobWizardStepFeaturesTabsItem from './JobWizardStepFeaturesTabsItem.vue'

import { useNewJobStore } from '~/store/newJob'

interface JobWizardStepFeaturesTabsProps {
  featuresLookup: Record<string, number>
}

defineProps<JobWizardStepFeaturesTabsProps>()

const activeTab = ref(JOB_FEATURE.REFRESH_HALFTIME)
const { t } = useI18n()
const store = useNewJobStore()

const selectedFeatures = computed({
  get() {
    return store.jobOffer.features || []
  },
  set(value) {
    store.updateJobOffer({ features: value })
  },
})

const selectedFeaturesLookup = computed(() => Object.fromEntries(selectedFeatures.value.map((feature: string) => [feature, true])))

const items = computed(() => [
  {
    id: JOB_FEATURE.SOCIAL_MEDIA_PUSH,
    label: t('new_job_landing.features.social_media_push'),
  },
  {
    id: JOB_FEATURE.REFRESH_HALFTIME,
    label: t('new_job_landing.features.halftime'),
  },
  {
    id: JOB_FEATURE.REFRESH_30,
    label: t('new_job_landing.features.refresh_30'),
  },
  {
    id: JOB_FEATURE.TOP_JOB,
    label: t('new_job_landing.features.top_job'),
  },
  {
    id: JOB_FEATURE.HIGHLIGHTING,
    label: t('new_job_landing.features.highlighting'),
  },
])

const promoCodeDiscount = computed(() => store.promoCodeDiscount)

const onChange = ({ id, isChecked }: { id: string; isChecked: boolean }) => {
  if (isChecked) {
    selectedFeatures.value = [...selectedFeatures.value, id]
  } else {
    selectedFeatures.value = selectedFeatures.value.filter((feature: string) => feature !== id)
  }
}
</script>

<template>
  <div class="grid grid-cols-12">
    <div class="col-span-12 flex flex-col gap-y-2 laptop:col-span-4">
      <JobWizardStepFeaturesTabsItem
        v-for="{ id, label } in items"
        :id="id"
        :key="id"
        :label="label"
        :is-selected="selectedFeaturesLookup[id]"
        :price="priceWithDiscount(featuresLookup[id], promoCodeDiscount)"
        @mouseover="activeTab = id"
        @change="(isChecked) => onChange({ id, isChecked })"
      />
    </div>

    <JobWizardStepFeaturesTabsContent :active-tab="activeTab" class="col-span-12 laptop:col-start-6" />
  </div>
</template>
